import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { t } from "../../i18n";

const SEARCH_HISTORY_KEY = "ft_search_history";

function safeReadSearchHistory(enabled = true) {
  if (!enabled) {
    return [];
  }

  if (typeof window === "undefined") {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(SEARCH_HISTORY_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.map((item) => String(item || "").trim()).filter(Boolean).slice(0, 8);
  } catch (_err) {
    return [];
  }
}

function safeWriteSearchHistory(items, enabled = true) {
  if (!enabled) {
    return;
  }

  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(items.slice(0, 8)));
  } catch (_err) {
    // Ignore localStorage write failures.
  }
}

export default function ProductSearchOverlay({ open, onClose, apiBaseUrl, privacyEnhanced = true }) {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [history, setHistory] = useState(() => safeReadSearchHistory(privacyEnhanced));
  const [products, setProducts] = useState([]);

  useEffect(() => {
    if (!privacyEnhanced) {
      setHistory([]);
      return;
    }

    if (open) {
      setHistory(safeReadSearchHistory(true));
    }
  }, [open, privacyEnhanced]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !apiBaseUrl) {
      return;
    }

    let cancelled = false;
    async function run() {
      try {
        const response = await fetch(`${apiBaseUrl}/products?lang=en&currency=USD`);
        if (!response.ok) {
          return;
        }

        const data = await response.json();
        if (!cancelled) {
          setProducts(Array.isArray(data.products) ? data.products : []);
        }
      } catch (_err) {
        if (!cancelled) {
          setProducts([]);
        }
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [open, apiBaseUrl]);

  const recommendKeywords = useMemo(() => {
    const tokens = [];
    products.slice(0, 16).forEach((item) => {
      if (item?.category) {
        tokens.push(String(item.category));
      }

      String(item?.name || "")
        .split(/\s+/)
        .map((part) => part.replace(/[^a-zA-Z0-9-]/g, "").trim())
        .filter((part) => part.length >= 4)
        .slice(0, 2)
        .forEach((part) => tokens.push(part));
    });

    const unique = Array.from(new Set(tokens));
    return unique.slice(0, 8);
  }, [products]);

  const normalizedKeyword = keyword.trim().toLowerCase();
  const quickResults = useMemo(() => {
    if (!normalizedKeyword) {
      return [];
    }

    return products
      .filter((item) => {
        const name = String(item?.name || "").toLowerCase();
        const category = String(item?.category || "").toLowerCase();
        const material = String(item?.material || "").toLowerCase();
        return name.includes(normalizedKeyword) || category.includes(normalizedKeyword) || material.includes(normalizedKeyword);
      })
      .slice(0, 6);
  }, [products, normalizedKeyword]);

  function pushHistory(nextKeyword) {
    if (!privacyEnhanced) {
      return;
    }

    const normalized = String(nextKeyword || "").trim();
    if (!normalized) {
      return;
    }

    const nextHistory = [normalized, ...history.filter((item) => item.toLowerCase() !== normalized.toLowerCase())].slice(0, 8);
    setHistory(nextHistory);
    safeWriteSearchHistory(nextHistory, privacyEnhanced);
  }

  function runSearch(nextKeyword) {
    const normalized = String(nextKeyword || "").trim();
    if (!normalized) {
      return;
    }

    pushHistory(normalized);
    onClose?.();
    navigate(`/products?keyword=${encodeURIComponent(normalized)}`);
  }

  function openProduct(productId) {
    if (!productId) {
      return;
    }

    if (keyword.trim()) {
      pushHistory(keyword);
    }

    onClose?.();
    navigate(`/products/${encodeURIComponent(productId)}`);
  }

  function clearHistory() {
    setHistory([]);
    safeWriteSearchHistory([], privacyEnhanced);
  }

  function submitSearch(event) {
    event.preventDefault();
    runSearch(keyword);
  }

  if (!open) {
    return null;
  }

  return (
    <div className="product-search-overlay" onClick={onClose}>
      <div className="product-search-modal" onClick={(event) => event.stopPropagation()}>
        <form className="product-search-head" onSubmit={submitSearch}>
          <input
            type="text"
            autoFocus
            placeholder={t("searchOverlayPlaceholder")}
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
          />
          <button type="submit" aria-label={t("searchOverlaySubmitAria")} title={t("searchOverlaySubmitTitle")}>⌕</button>
        </form>
        <p className="product-search-meta">
          {t("searchOverlayHintPrefix")} <kbd>Enter</kbd> {t("searchOverlayHintSuffix")}
        </p>
        <button type="button" className="product-search-close" onClick={onClose} aria-label={t("searchOverlayCloseAria")}>
          ×
        </button>

        <div className="product-search-panel">
          {!!quickResults.length && (
            <>
              <div className="product-search-title-row">
                <h3>{t("searchOverlayQuickResults")}</h3>
              </div>
              <ul className="product-search-results" aria-label={t("searchOverlayQuickResultsAria")}>
                {quickResults.map((product) => (
                  <li key={product.id}>
                    <button type="button" onClick={() => openProduct(product.id)}>
                      <strong>{product.name}</strong>
                      <span>{product.category || t("searchOverlayFallbackCategory")}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}

          <div className="product-search-title-row">
            <h3>{t("searchOverlayHistory")}</h3>
            {!!history.length && (
              <button type="button" className="product-search-link-btn" onClick={clearHistory}>
                {t("searchOverlayClear")}
              </button>
            )}
          </div>
          <div className="product-search-tags">
            {history.length ? (
              history.map((item) => (
                <button key={`history-${item}`} type="button" onClick={() => runSearch(item)}>{item}</button>
              ))
            ) : (
              <span className="product-search-empty">{t("searchOverlayNoHistory")}</span>
            )}
          </div>

          <div className="product-search-title-row">
            <h3>{t("searchOverlayRecommended")}</h3>
          </div>
          <div className="product-search-tags">
            {recommendKeywords.length ? (
              recommendKeywords.map((item) => (
                <button key={`recommend-${item}`} type="button" onClick={() => runSearch(item)}>{item}</button>
              ))
            ) : (
              <span className="product-search-empty">{t("searchOverlayFallbackKeyword")}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}